/* eslint-disable max-len */
/* eslint-disable @typescript-eslint/member-ordering */
import i18n from '@i18next/i18n';
import { RegionType } from '@src/slide/domain/selection/constant/region';
import { UIModelType } from '@src/slide/domain/ui-model/element-modeles/constant';
import { TENANT_CONFIG } from '@src/slide/infrastructure/module/dependent/config/tob/constant';
import { ShapeType } from '@src/slide/infrastructure/module/dependent/shape-info/graphic-type';
import { ptToCanvas } from '@src/slide/infrastructure/module/independent/unit';
import ActionSheet from '@tencent/dui-mobile/lib/components/ActionSheet';
import SnackbarMobile from '@tencent/dui-mobile/lib/components/Snackbar';
import { ImageSelector } from '@tencent/image-components';
import _debounce from 'lodash/debounce';
import { ImageActionSheetOptionID } from './enum';
import { IImageActionSheet, IImageActionSheetOption, IMobileActionSheetServiceDelegates } from './interface';

// 防止快速点击导致多次弹出 ActionSheet
const debouncedActionSheetShow = _debounce(ActionSheet.show, 1000, { leading: true, trailing: false });

export class ImageActionSheet implements IImageActionSheet {
    public constructor(private services: IMobileActionSheetServiceDelegates) {}

    private callActionFromPhotoAlbum = () => {
        try {
            const textEditor = this.services.editorInstanceService.getEditorInstance();
            // 触发当前正在编辑的文本框失焦
            textEditor?.triggerBlur();
            // selectLocalImageFile依赖jquery，jquery加载失败时方法有可能报错
            this.selectLocalImageFile(false);
        } catch {
            const inputObj = document.getElementById('select_pic_input');
            if (!inputObj) {
                return;
            }
            inputObj.click();
        }
    };

    private callActionFromTencentDocument = () => {
        this.services?.driveSelectorPanelService.promise()?.then((DriveSelectorPanelService) => {
            DriveSelectorPanelService?.show({
                serviceList: ['attachmentManager'],
                config: {
                    target: 'image',
                },
            });
        });
    };

    private callActionFromLocalFile = () => {
        document.getElementById('select_pic_input')?.click();
    };

    private callActionCamera = () => {
        try {
            const textEditor = this.services.editorInstanceService.getEditorInstance();
            // 触发当前正在编辑的文本框失焦
            textEditor?.triggerBlur();
            // selectLocalImageFile依赖jquery，jquery加载失败时方法有可能报错
            this.selectLocalImageFile(true);
        } catch {
            const inputObj = document.getElementById('select_pic_input');
            if (!inputObj) {
                return;
            }
            inputObj.setAttribute('capture', 'camera');
            inputObj.setAttribute('accept', 'image/camera');
            inputObj.click();
            inputObj.removeAttribute('capture');
            inputObj.setAttribute('accept', 'image/*');
        }
    };

    private options: IImageActionSheetOption[] = [
        {
            id: ImageActionSheetOptionID.FromPhotoAlbum,
            title: i18n.t('从相册'),
            callback: this.callActionFromPhotoAlbum,
        },
        {
            id: ImageActionSheetOptionID.FromTencentDocument,
            /* eslint-disable */
            title: TENANT_CONFIG.TENANT_DOC_NAME ? `从${TENANT_CONFIG.TENANT_DOC_NAME}` : i18n.t('从腾讯文档'),
            callback: this.callActionFromTencentDocument,
        },
        {
            id: ImageActionSheetOptionID.FromLocalFile,
            title: i18n.t('从本地文件'),
            callback: this.callActionFromLocalFile,
        },
        {
            id: ImageActionSheetOptionID.Camera,
            title: i18n.t('拍摄'),
            callback: this.callActionCamera,
        },
    ];

    public callActionSheet = (selectedActionList: ImageActionSheetOptionID[]) => {
        debouncedActionSheetShow({
            onClickAction: this.onClickAction,
            options: this.handleOptions(selectedActionList),
        });
    };

    public callActionDirect = (id: ImageActionSheetOptionID) => {
        this.onClickAction(id);
    };

    private handleOptions = (selectedActionList: ImageActionSheetOptionID[]) => selectedActionList
        .map(selectedID => this.options.find(option => option.id === selectedID))
        .filter(item => item) as IImageActionSheetOption[];

    private onClickAction = (id: ImageActionSheetOptionID) => {
        const option = this.options.find(option => option.id === id);
        option?.callback();
    };

    private selectLocalImageFile = (capture: boolean) => {
        const imageSelector = new ImageSelector();
        imageSelector.selectLocalImageFile({
            maxCount: capture ? 1 : 9,
            callback: (blobs) => {
                if (blobs?.length > 0) {
                    let arr = blobs;
                    if (!Array.isArray(blobs)) {
                        // 兼容返回值为没有forEach的类数组的场景
                        arr = Array.prototype.slice.call(blobs, null);
                    }
                    arr.forEach?.(value => value && this.insertImage([value]));
                    return;
                }
            },
            capture,
            errCallback: ({ retcode, msg }) => {
                switch (retcode) {
                    case 0:
                        window.log.error(msg);
                        break;
                    case 1:
                        SnackbarMobile.show({
                            message: i18n.t('无相册权限，请在系统设置开启。'),
                            duration: 3000,
                            autoClose: true,
                        });
                        break;
                    case 2:
                        SnackbarMobile.show({
                            message: i18n.t('图片上传失败，请重试。'),
                            duration: 3000,
                            autoClose: true,
                        });
                        break;
                }
            },
        });
    };

    private insertImage = async (blobs: any) => {
        const copyPasteImageService = await this.services?.copyPasteImageService?.promise();
        const ImageEditManager = copyPasteImageService?.ImageEditManager;
        const imageManager = ImageEditManager?.getInstance();
        const imgs = await imageManager?.createEditorImages(blobs);

        if (!this.services.selectionService.currentSlide) return;

        const { presentation } = this.services.uiDataService;
        const { currentSlideIndex, slides } = this.services.selectionService;
        if (!imgs || imgs.length === 0) {
            return;
        }
        const types: Array<string> = [];
        // eslint-disable-next-line @typescript-eslint/prefer-for-of
        for (let i = 0; i < imgs.length; i++) {
            const img = imgs[i];
            const width = ptToCanvas(img.width);
            const height = ptToCanvas(img.height);
            const { slideWidth, slideHeight } = presentation.props;
            let nextWidth = width;
            let nextHeight = height;
            if (width > slideWidth || height > slideHeight) {
                const ratioX = slideWidth / width;
                const ratioY = slideHeight / height;
                const ratio = ratioX < ratioY ? ratioX : ratioY;
                nextWidth = width * ratio;
                nextHeight = height * ratio;
            }
            const left = (slideWidth - nextWidth) / 2;
            const top = (slideHeight - nextHeight) / 2;
            img.style = {
                position: 'absolute',
                left,
                top,
                width: nextWidth,
                height: nextHeight,
                bgShape: ShapeType.rect,
                opacity: 1,
            };
            types.push(UIModelType.IMAGE);
        }
        const slideModel = slides[currentSlideIndex];
        slideModel.addElement(types, imgs);
        this.services.selectionService.lastMouseDownRegion = RegionType.EDITOR;
        this.services.selectionService.selectedSlides.clear();
        // 插入图片后，使页面滚动到画布中心
        window.dispatchEvent(new CustomEvent('resize', {
            detail: { scrollCenter: true },
        }));
    };
}
